import { useRef, useState } from "react";
import { saveBlissRoomPlan } from "../../services/blissRoomPlanService";
import { BLISS_DESIGN_SERVICES_TERMS } from "../../config/blissDesignServiceTerms";
import "./BlissPurchaseJourney.css";
import "./GuidedCheckout.css";
import { Check, PenTool, UserRound } from "lucide-react";

export default function RoomReviewCheckout({
  beforeCheckout,
  planId,
  quoteId,
  contact = {},
  onBack,
  afterPurchase = false,
  editorial = false,
}: {
  beforeCheckout?: (contact: {
    name: string;
    email: string;
    phone: string;
    zip: string;
  }) => Promise<string>;
  planId?: string;
  quoteId?: string;
  contact?: {
    name?: string;
    email?: string;
    phone?: string;
    zip?: string;
    zipCode?: string;
  };
  onBack?: () => void;
  afterPurchase?: boolean;
  editorial?: boolean;
}) {
  const savedPlan = useRef(planId);
  const form = useRef<HTMLFormElement>(null);
  const [details, setDetails] = useState({
      name: contact.name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      zip: contact.zip || contact.zipCode || "",
    }),
    [accepted, setAccepted] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [editing, setEditing] = useState(
    !contact.name || !contact.email || !(contact.zip || contact.zipCode),
  );
  return (
    <section
      className={`bpj-review guided-checkout room-review-compact${editorial ? " room-review-editorial" : ""}`}
    >
      <header className="gc-brand">
        {onBack && (
          <button
            type="button"
            className="gc-link"
            onClick={onBack}
            disabled={busy}
          >
            ‹ Back
          </button>
        )}
        <img src="/bliss-bedrooms-logo-transparent.png" alt="Bliss Bedrooms" />
      </header>
      {editorial ? (
        <>
          <div className="rr-introduction">
            <span className="gc-eyebrow">ANOTHER THOUGHTFUL WAY FORWARD</span>
            <h2>
              Would a smaller
              <br />
              first step help?
            </h2>
            <p>A little expert guidance, before you decide on the room.</p>
          </div>
          <figure className="rr-room-image">
            <img
              src="/bliss-basic/gallery/16-sage-room-white-bunk-styled-render.jpg"
              alt="Classic white bunk with layered linens and integrated stairs"
            />
            <figcaption>Styled inspiration</figcaption>
          </figure>
          <div className="rr-offer">
            <span className="gc-eyebrow">BLISS ROOM REVIEW</span>
            <strong className="rr-price">$149</strong>
            <h3>Clarity before a larger commitment.</h3>
            <p>
              A 30-minute consultation to discuss your room, priorities and
              possible directions.
            </p>
            <ul className="gc-benefits">
              {[
                "Room layout & bunk placement",
                "Dimensions, access & clearances",
                "Recommendations & next steps",
              ].map((label) => (
                <li key={label}>
                  <Check size={16} />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : (
        <>
          <span className="gc-eyebrow">BLISS ROOM REVIEW</span>
          <h2>A little expert guidance.</h2>
          <p className="gc-lead">
            {afterPurchase
              ? "Review your chosen bunk with a Bliss designer."
              : "Plan your room with a Bliss designer."}
          </p>
          <div className="gc-service gc-card">
            <div className="gc-service-heading">
              <PenTool size={28} />
              <div>
                <h3>Your Room Review</h3>
                <small>30 minutes · Personal design advice</small>
              </div>
              <strong>$149</strong>
            </div>
            <ul className="gc-benefits">
              {[
                "Room layout & bunk placement",
                "Dimensions, access & clearances",
                "Recommendations & next steps",
              ].map((label) => (
                <li key={label}>
                  <Check size={16} />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
      <form
        ref={form}
        onInvalid={() => setEditing(true)}
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy || !accepted) return;
          setBusy(true);
          setError("");
          try {
            let savedId = savedPlan.current;
            if (beforeCheckout) {
              savedId = await beforeCheckout(details);
              savedPlan.current = savedId;
            } else if (!quoteId || planId) {
              const saved = await saveBlissRoomPlan(
                { contact: details },
                savedPlan.current,
              );
              if (!saved.plan)
                throw new Error(saved.error || "Could not save your room.");
              savedId = saved.plan.id;
              savedPlan.current = savedId;
            }
            const response = await fetch("/api/create-design-checkout", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                offerId: "room_review",
                name: details.name,
                email: details.email,
                phone: details.phone,
                projectZip: details.zip,
                planId: savedId,
                quoteRequestId: quoteId,
                paymentRail: "card",
              }),
            });
            const result = await response.json();
            if (!response.ok || !result.checkoutUrl)
              throw new Error(
                result.error || "Could not open secure checkout.",
              );
            window.location.assign(result.checkoutUrl);
          } catch (e) {
            setError((e as Error).message);
            setBusy(false);
          }
        }}
      >
        <div className="gc-section-title">
          <h3>Your details</h3>
          <button
            type="button"
            className="gc-link"
            disabled={busy}
            onClick={() => {
              if (
                editing &&
                !Array.from(
                  form.current?.querySelectorAll<HTMLInputElement>(
                    ".bpj-fields input",
                  ) ?? [],
                ).every((input) => input.reportValidity())
              )
                return;
              setEditing(!editing);
            }}
            aria-expanded={editing}
          >
            {editing ? "Done" : "Edit"}
          </button>
        </div>
        {!editing && (
          <div className="gc-contact gc-card">
            <UserRound size={28} />
            <div>
              <strong>{details.name}</strong>
              <span>{details.email}</span>
              <span>
                {details.phone}
                {details.phone ? " · " : ""}ZIP {details.zip}
              </span>
            </div>
          </div>
        )}
        <div className="bpj-fields" hidden={!editing}>
          {(["name", "email", "phone", "zip"] as const).map((key) => (
            <label key={key}>
              {
                {
                  name: "Your name",
                  email: "Email",
                  phone: "Phone",
                  zip: "Project ZIP",
                }[key]
              }
              <input
                disabled={busy}
                required={key !== "phone"}
                type={
                  key === "email" ? "email" : key === "phone" ? "tel" : "text"
                }
                autoComplete={
                  {
                    name: "name",
                    email: "email",
                    phone: "tel",
                    zip: "postal-code",
                  }[key]
                }
                pattern={key === "zip" ? "[0-9]{5}" : undefined}
                maxLength={key === "zip" ? 5 : 120}
                value={details[key]}
                onChange={(e) =>
                  setDetails({ ...details, [key]: e.target.value })
                }
              />
            </label>
          ))}
        </div>
        <details className="gc-disclosure">
          <summary>What to expect</summary>
          <p>
            Your designer reviews your room, floor plans and measurements with
            you, then recommends next steps for Bliss Basic or Bliss Custom.
          </p>
          <p>Pay securely, then choose your appointment time.</p>
        </details>
        <details className="gc-disclosure" id="room-review-terms">
          <summary>Room Review service terms</summary>
          {BLISS_DESIGN_SERVICES_TERMS.sections
            .filter(
              (s) =>
                s.heading.startsWith("Room Consultation") ||
                s.heading === "Payment methods",
            )
            .map((s) => (
              <p key={s.heading}>{s.body}</p>
            ))}
          <p>
            Detailed custom plans are a separate service, quoted at $1,500 and
            up depending on scope. The review itself does not commit you to a
            build or a plan package.
          </p>
        </details>
        <label className="bpj-check">
          <input
            disabled={busy}
            required
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />{" "}
          I agree to the Room Review service terms.
        </label>
        {error && <p role="alert">{error}</p>}
        <p className="gc-note">Choose your appointment after payment.</p>
        <footer className="gc-footer">
          <div>
            <small>Room Review</small>
            <strong>$149</strong>
          </div>
          <button className="gc-primary" disabled={busy || !accepted}>
            {busy ? "Opening Payment…" : "Book my Room Review →"}
          </button>
        </footer>
      </form>
    </section>
  );
}
